import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authToken from 'src/services/auth-header';
import PlansAndBillingService from 'src/services/plans_and_billing';
import { useAppDispatch } from 'src/store';
import { FeatureItem } from '../ChooseYourPlan';

type PlanFeatures = {
  qr_codes: boolean;
  dedicated_oppy_landing_page: boolean;
  // ai_assistants_for_scheduling_and_support: number;
  credits: number;
  price_per_credit: number;
  dedicated_phone_line_per_oppy_price: number;
  dedicated_phone_line_per_oppy: number;
  import_web_pages_per_oppy: number;
  scheduled_sms_sending: boolean;
  text_messaging: boolean;
  smart_follow_up: boolean;
  priority_ai_response_queueing_and_delivery: boolean;
  contact_and_lead_dashboard: boolean;
  bulk_ai_personalized_follow_up_messages: boolean;
  you_in_the_loop_dashboard: boolean;
  traffic_aware_instant_booking: boolean;
  custom_rules_and_prompts_for_lead_capture: boolean;
  anonymous_messages_training_exclusion: boolean;
  early_access_to_new_features: boolean;
  integrations: boolean;
  custom_email_domain: boolean;
  ai_voice_features: boolean;
  custom_setup_and_onboarding: boolean;
  priority_support: boolean;
  pricing_monthly: number;
  display_pricing: string;
  oppies?: number;
  daily_credits?: number;
  ai_evaluation?: boolean;
  document_analysis?: boolean;
  inbound_messages?: boolean;
  outbound_messages?: boolean;
};

type ApiPlan = {
  id: number;
  name: string;
  tier: string;
  amount: string;
  features: PlanFeatures;
  description: string;
  display_pricing: string;
};

type ApiResponse = {
  current_plan: ApiPlan & {
    stripe_product_id: string;
    stripe_price_id: string;
    active: boolean;
    created_at: string;
    updated_at: string;
  };
  available_plans: ApiPlan[];
};

type Plan = {
  id: number;
  title: string;
  price: string;
  priceDetail: string;
  buttonText: string;
  buttonVariant: 'text' | 'outlined' | 'contained';
  badge?: string;
  badgeStyle?: React.CSSProperties;
  trialInfo?: string;
  features: string[];
  theme: 'light' | 'dark';
  tier: string;
  isCurrentPlan: boolean;
};

// Função para gerar features baseadas nos dados da API
const generateFeaturesFromApi = (
  plan: ApiPlan,
  isCurrentPlan: boolean,
  allPlans: ApiPlan[]
): string[] => {
  const features: string[] = [];

  if (!plan || !plan.features) {
    console.error('Invalid plan or plan features:', plan);
    return features;
  }

  const { features: planFeatures } = plan;

  // Validate planFeatures structure
  if (!planFeatures || typeof planFeatures !== 'object') {
    console.error('Invalid planFeatures structure:', planFeatures);
    return features;
  }

  // Encontrar o plano anterior para comparação
  const planIndex = allPlans.findIndex((p) => p.id === plan.id);
  const previousPlan = planIndex > 0 ? allPlans[planIndex - 1] : null;

  // Se é o primeiro plano (Basic), mostrar todas as features
  if (!previousPlan) {
    if (planFeatures.oppies && planFeatures.oppies > 0) {
      features.push(`Up to ${planFeatures.oppies} Oppy Assistants`);
    } else {
      features.push('Up to 2 Oppy Assistants');
    }

    if (
      planFeatures.dedicated_phone_line_per_oppy &&
      planFeatures.dedicated_phone_line_per_oppy > 0
    ) {
      features.push(
        `Dedicated phone line for $${planFeatures.dedicated_phone_line_per_oppy_price || 5
        }/mo`
      );
    } else {
      features.push('Dedicated phone line for $5/mo');
    }

    if (planFeatures.qr_codes === true) {
      features.push('QR Codes');
    }

    if (planFeatures.dedicated_oppy_landing_page === true) {
      features.push('Dedicated Oppy Landing Page');
    }

    if (planFeatures.outbound_messages === true) {
      features.push('Outbound messages');
    }

    if (
      planFeatures.import_web_pages_per_oppy &&
      planFeatures.import_web_pages_per_oppy > 0
    ) {
      features.push(
        `Up to ${planFeatures.import_web_pages_per_oppy} page imports`
      );
    }

    // Features básicas que todos os planos têm
    if (planFeatures.inbound_messages === true) {
      features.push('Inbound messages');
    }
  } else {
    // Para planos subsequentes, mostrar apenas as features adicionais
    const previousFeatures = previousPlan.features;

    // Comparar Oppy Assistants
    if (
      planFeatures.oppies &&
      planFeatures.oppies > (previousFeatures.oppies || 0)
    ) {
      if (planFeatures.oppies === 99) {
        features.push('Unlimited Oppy Assistants');
      } else {
        features.push(`Up to ${planFeatures.oppies} Oppy Assistants`);
      }
    }

    // Comparar page imports
    if (
      planFeatures.import_web_pages_per_oppy &&
      planFeatures.import_web_pages_per_oppy >
      (previousFeatures.import_web_pages_per_oppy || 0)
    ) {
      features.push(
        `Up to ${planFeatures.import_web_pages_per_oppy} page imports`
      );
    }

    // Features que não existiam no plano anterior
    if (
      previousFeatures.scheduled_sms_sending !== true &&
      planFeatures.scheduled_sms_sending === true
    ) {
      features.push('Scheduled SMS sending');
    }

    if (
      previousFeatures.text_messaging !== true &&
      planFeatures.text_messaging === true
    ) {
      features.push('Text messaging');
    }

    if (
      previousFeatures.smart_follow_up !== true &&
      planFeatures.smart_follow_up === true
    ) {
      features.push('Smart follow-up');
    }

    if (
      previousFeatures.ai_voice_features !== true &&
      planFeatures.ai_voice_features === true
    ) {
      features.push('AI voice features');
    }

    if (
      previousFeatures.integrations !== true &&
      planFeatures.integrations === true
    ) {
      features.push('CRM integration');
    }

    if (
      previousFeatures.outbound_messages !== true &&
      planFeatures.outbound_messages === true
    ) {
      features.push('Outbound messages');
    }

    if (
      previousFeatures.custom_rules_and_prompts_for_lead_capture !== true &&
      planFeatures.custom_rules_and_prompts_for_lead_capture === true
    ) {
      features.push('Custom prompts for lead capture');
    }

    // Features Enterprise que não existiam antes
    if (
      previousFeatures.custom_email_domain !== true &&
      planFeatures.custom_email_domain === true
    ) {
      features.push('Custom email domain');
    }

    if (
      previousFeatures.custom_setup_and_onboarding !== true &&
      planFeatures.custom_setup_and_onboarding === true
    ) {
      features.push('Custom setup & onboarding');
    }

    if (
      previousFeatures.priority_support !== true &&
      planFeatures.priority_support === true
    ) {
      features.push('Priority support');
    }

    if (
      previousFeatures.ai_evaluation !== true &&
      planFeatures.ai_evaluation === true
    ) {
      features.push('AI evaluations');
    }

    if (
      previousFeatures.document_analysis !== true &&
      planFeatures.document_analysis === true
    ) {
      features.push('Document analysis');
    }

    if (
      previousFeatures.inbound_messages !== true &&
      planFeatures.inbound_messages === true
    ) {
      features.push('Inbound messages');
    }

    if (
      previousFeatures.early_access_to_new_features !== true &&
      planFeatures.early_access_to_new_features === true
    ) {
      features.push('Early access to new features');
    }

    if (
      previousFeatures.anonymous_messages_training_exclusion !== true &&
      planFeatures.anonymous_messages_training_exclusion === true
    ) {
      features.push('White label features');
    }

    if (
      previousFeatures.priority_ai_response_queueing_and_delivery !== true &&
      planFeatures.priority_ai_response_queueing_and_delivery === true
    ) {
      features.push('Priority AI response queueing and delivery');
    }

    if (
      previousFeatures.contact_and_lead_dashboard !== true &&
      planFeatures.contact_and_lead_dashboard === true
    ) {
      features.push('Contact and lead dashboard');
    }

    if (
      previousFeatures.bulk_ai_personalized_follow_up_messages !== true &&
      planFeatures.bulk_ai_personalized_follow_up_messages === true
    ) {
      features.push('Bulk AI personalized follow-up messages');
    }

    if (
      previousFeatures.you_in_the_loop_dashboard !== true &&
      planFeatures.you_in_the_loop_dashboard === true
    ) {
      features.push('You in the loop dashboard');
    }

    if (
      previousFeatures.traffic_aware_instant_booking !== true &&
      planFeatures.traffic_aware_instant_booking === true
    ) {
      features.push('Traffic aware instant booking');
    }

    if (
      previousFeatures.dedicated_oppy_landing_page !== true &&
      planFeatures.dedicated_oppy_landing_page === true
    ) {
      features.push('Dedicated Oppy Landing Page');
    }
  }

  return features;
};

// Função para converter dados da API para o formato do componente
const convertApiPlansToComponentPlans = (apiData: ApiResponse): Plan[] => {
  const { current_plan, available_plans } = apiData;

  if (!available_plans || !Array.isArray(available_plans)) {
    console.error('Invalid available_plans:', available_plans);
    return [];
  }

  // current_plan can be null in onboarding - this is normal

  return available_plans
    .map((apiPlan, index) => {
      console.log(`Processing plan ${index}:`, apiPlan);

      if (!apiPlan) {
        console.error('Invalid apiPlan:', apiPlan);
        return null;
      }

      if (!apiPlan.features) {
        console.error('Plan missing features:', apiPlan);
        return null;
      }

      const isCurrentPlan = current_plan
        ? apiPlan.id === current_plan.id
        : false;
      console.log(`Plan ${apiPlan.name} is current plan:`, isCurrentPlan);

      const features = generateFeaturesFromApi(
        apiPlan,
        isCurrentPlan,
        available_plans
      );

      console.log(`Generated features for ${apiPlan.name}:`, features);

      // Determinar badge baseado no tier
      let badge: string | undefined;
      let badgeStyle: React.CSSProperties | undefined;

      if ((apiPlan.tier || 'basic') === 'pro') {
        badge = 'Most Popular';
        badgeStyle = { backgroundColor: '#15803D', color: '#fff' };
      } else if ((apiPlan.tier || 'basic') === 'enterprise') {
        badge = 'Best Value';
        badgeStyle = { backgroundColor: '#4949C8', color: '#fff' };
      }

      // Determinar button text e variant
      let buttonText = `Start with ${apiPlan.name || 'Plan'}`;
      let buttonVariant: 'text' | 'outlined' | 'contained' = 'contained';

      if ((apiPlan.tier || 'basic') === 'basic') {
        buttonText = 'Start free trial';
        buttonVariant = 'outlined';
      }

      return {
        id: apiPlan.id || 0,
        title: apiPlan.name || 'Unknown Plan',
        price: apiPlan.amount || '0',
        priceDetail: `${(apiPlan.features.credits || 0).toLocaleString()} + ${apiPlan.features.daily_credits || 300
          } daily credits`,
        buttonText,
        buttonVariant,
        badge,
        badgeStyle,
        trialInfo:
          (apiPlan.tier || 'basic') === 'basic'
            ? '14-day free trial • No charges today'
            : undefined,
        features,
        theme: 'light' as const,
        tier: apiPlan.tier || 'basic',
        isCurrentPlan,
      };
    })
    .filter(Boolean);
};

interface ChoosePlanCardsProps {
  subscriptionCheckoutUrl?: string;
  pendingRegistrationData?: any;
  disableRedirect?: boolean; // New prop to disable automatic redirect
}

export const ChoosePlanCards = ({
  subscriptionCheckoutUrl,
  pendingRegistrationData,
  disableRedirect,
}: ChoosePlanCardsProps) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<any>(null);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching plans...');

      // Check if user is authenticated and use appropriate endpoint
      const isAuthenticated = !!authToken();
      let response;

      if (isAuthenticated) {
        // Use private endpoint for authenticated users
        console.log('User is authenticated, using private endpoint');
        response = await PlansAndBillingService.getAvailablePlansForUser();
      } else {
        // Use public endpoint for unauthenticated users
        console.log('User is not authenticated, using public endpoint');
        response = await PlansAndBillingService.getAllPlans();
      }

      console.log('API Response:', response);

      if (!response) {
        console.error('No response received');
        setError('No response from server');
        return;
      }

      // Handle both public and private endpoint responses
      let availablePlans =
        response.available_plans || response.subscription_plans;
      let currentPlan = response.current_plan || null;

      if (currentPlan && !disableRedirect) {
        navigate('/channels');
      }

      if (!availablePlans || !Array.isArray(availablePlans)) {
        console.error(
          'Invalid API response structure - missing available_plans or subscription_plans:',
          response
        );
        setError('Invalid response structure from server');
        return;
      }

      // For public endpoint (registration flow), there's no current_plan
      // For private endpoint (existing users), there might be a current_plan
      const effectiveCurrentPlan = subscriptionCheckoutUrl ? null : currentPlan;

      if (subscriptionCheckoutUrl) {
        console.log(
          'subscriptionCheckoutUrl detected - ignoring current_plan to allow plan selection'
        );
        console.log('Original current_plan:', currentPlan);
        console.log('Effective current_plan:', effectiveCurrentPlan);
      }

      const convertedPlans = convertApiPlansToComponentPlans({
        available_plans: availablePlans,
        current_plan: effectiveCurrentPlan,
      });
      console.log('Converted plans:', convertedPlans);
      // keep only the Enterprise Semi Managed plan
      setPlans(
        convertedPlans.filter(
          (p: any) =>
            p.tier === 'enterprise_semi_managed' ||
            p.stripe_product_id === 'prod_SmDHQl2LahfigR'
        )
      );
      setCurrentPlan(effectiveCurrentPlan);

      console.log('Current plan from API:', response.current_plan);
      console.log(
        'Converted plans with current plan flag:',
        convertedPlans.map((p) => ({
          name: p.title,
          isCurrentPlan: p.isCurrentPlan,
          features: p.features,
        }))
      );
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const subscribeToPlan = async (planId: number, currentPlan: any = null) => {
    try {
      // If we have pending registration data, execute registration first
      if (pendingRegistrationData) {
        console.log('Executing registration with plan_id:', planId);

        try {
          // Import the register action from auth slice
          const { register } = await import('src/features/auth/auth.slice');
          const { trackAnalytics } = await import(
            'src/features/analytics/analytics.slice'
          );

          // For basic plan trial, don't pass plan_id to trigger trial setup
          // For other plans, pass plan_id to create subscription
          const basicPlan = plans.find((plan) => plan.tier === 'basic');
          const isBasicPlanTrial = basicPlan && planId === basicPlan.id;

          const registrationData = isBasicPlanTrial
            ? pendingRegistrationData
            : { ...pendingRegistrationData, plan_id: planId };

          const registrationResult = await dispatch(
            register(registrationData)
          ).unwrap();

          console.log('Registration successful:', registrationResult);

          // Clear pending registration data
          sessionStorage.removeItem('pendingRegistration');

          // Track analytics
          const actionDescription = isBasicPlanTrial
            ? `User with email ${pendingRegistrationData.email} created account with trial`
            : `User with email ${pendingRegistrationData.email} created account with plan ${planId}`;

          dispatch(
            trackAnalytics({
              action_name: 'user_registered',
              payload: {
                description: actionDescription,
              },
            })
          );

          // Check if registration returned a Stripe checkout URL
          if (registrationResult.subscription_checkout_url) {
            console.log(
              'Redirecting to Stripe checkout:',
              registrationResult.subscription_checkout_url
            );
            window.location.href = registrationResult.subscription_checkout_url;
          } else {
            // If no checkout URL, redirect to channels
            console.log('No checkout URL, redirecting to channels');
            window.location.href = '/channels';
          }
          return;
        } catch (registrationError) {
          console.error('Registration failed:', registrationError);

          // Check if this is a "user already exists" error
          const errorMessage =
            registrationError?.message || registrationError?.toString() || '';
          const isUserAlreadyExists =
            errorMessage.includes('User with this email already exists') ||
            errorMessage.includes('USER_ALREADY_EXISTS');

          if (isUserAlreadyExists) {
            // Clear pending registration data
            sessionStorage.removeItem('pendingRegistration');

            // Store the error message in sessionStorage to display on login page
            sessionStorage.setItem(
              'loginError',
              'User with this email already exists. Please log in instead.'
            );

            // Redirect to login page
            window.location.href = '/login';
            return;
          }

          // For other errors, fall back to normal subscription flow
          console.error('Registration error details:', registrationError);
        }
      }

      // Se temos uma URL de checkout pré-gerada, é o plano básico, e o usuário não tem plano atual (onboarding), use-a
      const basicPlan = plans.find((plan) => plan.tier === 'basic');
      if (
        subscriptionCheckoutUrl &&
        basicPlan &&
        planId === basicPlan.id &&
        currentPlan === null
      ) {
        console.log(
          'Using pre-generated checkout URL for basic plan during onboarding'
        );
        window.location.href = subscriptionCheckoutUrl;
        return;
      }

      // Check if user is authenticated and use appropriate endpoint
      const isAuthenticated = !!authToken();
      let response;

      if (isAuthenticated) {
        // For authenticated users, check if they need to upgrade or subscribe
        if (currentPlan && currentPlan.id !== planId) {
          // User is upgrading to a different plan
          response = await PlansAndBillingService.upgradeToPlan(planId);
        } else {
          // User is subscribing to a new plan
          response = await PlansAndBillingService.subscribeToPlan(planId);
        }
      } else {
        // For unauthenticated users, use the public subscription endpoint
        response = await PlansAndBillingService.subscribeToPlan(planId);
      }

      if (response.success && response.checkout_url) {
        // Abrir a URL de checkout na mesma aba
        window.location.href = response.checkout_url;
        console.log('Subscribe to plan response:', response);
      } else {
        console.error('Invalid response from subscribeToPlan:', response);
      }
    } catch (error) {
      console.error('Error subscribing to plan:', error);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Debug animation
  console.log('ChoosePlanCards rendered, plans:', plans);

  // Breakpoints customizados
  const is1 = useMediaQuery('(max-width:630px)');
  const is2 = useMediaQuery('(min-width:631px) and (max-width:980px)');
  const is3 = useMediaQuery('(min-width:981px) and (max-width:1400px)');
  const is4 = useMediaQuery('(min-width:1401px)');

  // Cards por slide conforme breakpoints
  let cardsPerSlide = 1;
  if (is2) cardsPerSlide = 2;
  else if (is3) cardsPerSlide = 3;
  else if (is4) cardsPerSlide = 4;

  const isCarousel = useMediaQuery('(min-width:631px) and (max-width:1400px)');

  const [current, setCurrent] = useState(0);

  const next = () => setCurrent((prev) => (prev + 1) % plans.length);
  const prev = () =>
    setCurrent((prev) => (prev - 1 + plans.length) % plans.length);

  const [activePlanIdx, setActivePlanIdx] = useState(0);

  const getVisibleCards = () => {
    let visible = [];
    for (let i = 0; i < cardsPerSlide; i++) {
      const planIdx = (current + i) % plans.length;
      visible.push({ ...plans[planIdx], planIdx });
    }
    return visible;
  };

  const renderFeatures = (features: string[]) => {
    if (
      features.length > 0 &&
      (features[0].startsWith('Everything in Basic') ||
        features[0].startsWith('Everything in The Ice Breaker') ||
        features[0].startsWith('Everything in Pro'))
    ) {
      return (
        <>
          <Typography
            variant="body2"
            sx={{
              color: darkMode ? theme.palette.text.primary : '#111827',
              fontWeight: 700,
              mb: 1,
              fontFamily: 'Space Grotesk',
            }}
          >
            {features[0]}
          </Typography>
          {features.slice(1).map((feature, i) => (
            <FeatureItem key={i}>{feature}</FeatureItem>
          ))}
        </>
      );
    }
    return features.map((feature, i) => (
      <FeatureItem key={i}>{feature}</FeatureItem>
    ));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Loading plans...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (plans.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>No plans available</Typography>
      </Box>
    );
  }

  const hasCurrentPlan = plans.some((p) => p.isCurrentPlan);

  if (is1 || is2) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: undefined,
          '@keyframes fadeInUp': {
            '0%': {
              opacity: 0,
              transform: 'translateY(50px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '1920px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              alignItems: 'center',
            }}
          >
            {plans.map((plan, idx) => {
              const isPlansAndBilling = hasCurrentPlan;
              const isCurrentPlan = plan.isCurrentPlan;
              // Determine button text and color
              let buttonText = plan.buttonText;
              let buttonColor = {};
              if (isPlansAndBilling) {
                if (isCurrentPlan) {
                  buttonText = 'Your Current Plan';
                } else if (idx < activePlanIdx) {
                  buttonText = 'Downgrade';
                  buttonColor = {
                    backgroundColor: darkMode
                      ? theme.palette.background.paper
                      : '#fff',
                    color: '#4949C8',
                    borderColor: '#4949C8',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    fontWeight: 400,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#F6F6FF',
                      color: '#4949C8',
                      borderColor: '#4949C8',
                      boxShadow: 'none',
                    },
                  };
                } else {
                  buttonText = 'Upgrade';
                  buttonColor = {
                    backgroundColor: '#4949C8',
                    color: '#fff',
                    borderColor: '#4949C8',
                    '&:hover': {
                      backgroundColor: '#3A3A9E',
                      color: '#fff',
                      borderColor: '#4949C8',
                    },
                  };
                }
              } else {
                // Onboarding: botão do Basic outlined, os demais contained
                if (plan.tier === 'basic') {
                  buttonColor = {
                    backgroundColor: '#fff',
                    color: '#4949C8',
                    borderColor: '#4949C8',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    '&:hover': {
                      backgroundColor: '#F6F6FF',
                      color: '#4949C8',
                      borderColor: '#4949C8',
                    },
                  };
                } else {
                  buttonColor = {
                    backgroundColor: '#4949C8',
                    color: '#fff',
                    borderColor: '#4949C8',
                    '&:hover': {
                      backgroundColor: '#3A3A9E',
                      color: '#fff',
                      borderColor: '#4949C8',
                    },
                  };
                }
              }
              return (
                <Card
                  key={plan.title}
                  sx={{
                    width: '100%',
                    minHeight: '800px',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    boxShadow: darkMode
                      ? '0 1px 1px -3px rgba(0,0,0,0.3), 0 3px 6px -2px rgba(0,0,0,0.2)'
                      : '0 1px 1px -3px rgba(0,0,0,0.1), 0 3px 6px -2px rgba(0,0,0,0.01)',
                    position: 'relative',
                    backgroundColor:
                      hasCurrentPlan && isCurrentPlan
                        ? darkMode
                          ? theme.palette.grey[800]
                          : '#F8F8F8'
                        : darkMode
                          ? theme.palette.background.paper
                          : '#fff',
                    color: darkMode ? theme.palette.text.primary : '#111827',
                    border: darkMode
                      ? `1px solid ${theme.palette.divider}`
                      : '1px solid #E5E7EB',
                    opacity: 0,
                    transform: 'translateY(50px)',
                    animation:
                      idx === 0
                        ? 'fadeInUp 1s ease-out 0.5s both'
                        : `fadeInUp 0.8s ease-out ${0.2 + idx * 0.1}s both`,
                    '@keyframes fadeInUp': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(50px)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0)',
                      },
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 5,
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        display: 'flex',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h5"
                          component="h2"
                          gutterBottom
                          sx={{
                            fontWeight: 'bold',
                            fontSize: 24,
                            fontFamily: 'Power Grotesk',
                            mb: 0,
                            color: darkMode
                              ? theme.palette.text.primary
                              : '#111827',
                          }}
                        >
                          {plan.title}
                        </Typography>
                        {plan.badge && (
                          <Chip
                            label={plan.badge}
                            sx={{
                              fontWeight: 'bold',
                              ...plan.badgeStyle,
                              px: 0,
                              fontSize: 13,
                              borderRadius: 16,
                              letterSpacing: 0.5,
                              height: 26,
                            }}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          my: 2,
                        }}
                      >
                        <Typography
                          variant="h3"
                          component="p"
                          sx={{
                            fontWeight: 'bold',
                            fontSize: { xs: 32, sm: 36, md: 40 },
                            mr: 1,
                            fontFamily: 'Space Grotesk',
                            color: darkMode
                              ? theme.palette.text.primary
                              : '#111827',
                          }}
                        >
                          $
                          {Number(plan.price) % 1 === 0
                            ? Number(plan.price).toFixed(0)
                            : Number(plan.price).toFixed(2)}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: darkMode
                              ? theme.palette.text.secondary
                              : '#6B7280',
                            fontSize: { xs: 16, sm: 17, md: 18 },
                          }}
                        >
                          /month
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          minHeight: '3em',
                          color: darkMode
                            ? theme.palette.text.secondary
                            : '#6B7280',
                          fontSize: { xs: 14, sm: 15, md: 16 },
                        }}
                      >
                        {plan.priceDetail}
                      </Typography>
                    </Box>
                    <Box sx={{ my: 4 }}>
                      <Button
                        fullWidth
                        variant={plan.buttonVariant}
                        size="large"
                        disabled={isPlansAndBilling && isCurrentPlan}
                        onClick={
                          isPlansAndBilling && !isCurrentPlan
                            ? () => setActivePlanIdx(idx)
                            : !isPlansAndBilling
                              ? () => subscribeToPlan(plan.id, currentPlan)
                              : undefined
                        }
                        sx={{
                          ...buttonColor,
                          textTransform: 'none',
                          py: 2,
                          fontFamily: 'Space Grotesk',
                          borderRadius: 1.3,
                          fontSize: 16,
                        }}
                      >
                        {buttonText}
                      </Button>
                      {!isPlansAndBilling && plan.trialInfo && (
                        <Typography
                          variant="caption"
                          display="block"
                          align="center"
                          sx={{
                            mt: 1,
                            color: darkMode
                              ? theme.palette.text.secondary
                              : '#6B7280',
                            fontSize: { xs: 12, sm: 13 },
                          }}
                        >
                          {plan.trialInfo}
                        </Typography>
                      )}
                    </Box>
                    <Box>{renderFeatures(plan.features)}</Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: hasCurrentPlan ? 'start' : 'center',
        width: hasCurrentPlan ? '100%' : undefined,
        '@keyframes fadeInUp': {
          '0%': {
            opacity: 0,
            transform: 'translateY(50px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: hasCurrentPlan ? '100%' : '1920px',
        }}
      >
        {isCarousel && plans.length >= 1 ? (
          <Box
            sx={{
              position: 'relative',
              maxWidth:
                cardsPerSlide === 4
                  ? 1400
                  : cardsPerSlide === 3
                    ? 1200
                    : cardsPerSlide === 2
                      ? 800
                      : '100%',
              width: '100%',
              pb: 4,
              transition: 'max-width 0.2s',
              px: { xs: 1, sm: 2 },
            }}
          >
            <IconButton
              onClick={prev}
              sx={{
                position: 'absolute',
                left: -16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: darkMode ? theme.palette.background.paper : '#fff',
                zIndex: 2,
                boxShadow: 2,
                '&:hover': {
                  bgcolor: darkMode ? theme.palette.grey[700] : '#f3f4f6',
                },
              }}
            >
              <ArrowBackIosNewIcon sx={{ color: '#4A49C8' }} fontSize="small" />
            </IconButton>
            <IconButton
              onClick={next}
              sx={{
                position: 'absolute',
                right: -16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: darkMode ? theme.palette.background.paper : '#fff',
                zIndex: 2,
                boxShadow: 2,
                '&:hover': {
                  bgcolor: darkMode ? theme.palette.grey[700] : '#f3f4f6',
                },
              }}
            >
              <ArrowForwardIosIcon sx={{ color: '#4A49C8' }} fontSize="small" />
            </IconButton>
            <Box
              sx={{
                minHeight: 800,
                display: 'flex',
                gap: 3,
                justifyContent: 'center',
                alignItems: 'stretch',
                height: 800,
                transition: 'all 0.3s',
              }}
            >
              {getVisibleCards().map((planObj, idx) => {
                const plan = planObj;
                const planIdx = plan.planIdx;
                const isPlansAndBilling = hasCurrentPlan;
                const isCurrentPlan = plan.isCurrentPlan;
                // Determine button text and color
                let buttonText = plan.buttonText;
                let buttonColor = {};
                if (isPlansAndBilling) {
                  if (isCurrentPlan) {
                    buttonText = 'Your Current Plan';
                  } else if (planIdx < activePlanIdx) {
                    buttonText = 'Downgrade';
                    buttonColor = {
                      backgroundColor: darkMode
                        ? theme.palette.background.paper
                        : '#fff',
                      color: '#4949C8',
                      borderColor: '#4949C8',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      fontWeight: 400,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: '#F6F6FF',
                        color: '#4949C8',
                        borderColor: '#4949C8',
                        boxShadow: 'none',
                      },
                    };
                  } else {
                    buttonText = 'Upgrade';
                    buttonColor = {
                      backgroundColor: '#4949C8',
                      color: '#fff',
                      borderColor: '#4949C8',
                      '&:hover': {
                        backgroundColor: '#3A3A9E',
                        color: '#fff',
                        borderColor: '#4949C8',
                      },
                    };
                  }
                } else {
                  // Onboarding: botão do Basic outlined, os demais contained
                  if (plan.tier === 'basic') {
                    buttonColor = {
                      backgroundColor: '#fff',
                      color: '#4949C8',
                      borderColor: '#4949C8',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      '&:hover': {
                        backgroundColor: '#F6F6FF',
                        color: '#4949C8',
                        borderColor: '#4949C8',
                      },
                    };
                  } else {
                    buttonColor = {
                      backgroundColor: '#4949C8',
                      color: '#fff',
                      borderColor: '#4949C8',
                      '&:hover': {
                        backgroundColor: '#3A3A9E',
                        color: '#fff',
                        borderColor: '#4949C8',
                      },
                    };
                  }
                }
                return (
                  <Card
                    key={plan.title}
                    sx={{
                      width:
                        cardsPerSlide === 4
                          ? 320
                          : cardsPerSlide === 3
                            ? 350
                            : cardsPerSlide === 2
                              ? 370
                              : '100%',
                      minWidth: 0,
                      maxWidth: '100%',
                      minHeight: '800px',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      boxShadow: darkMode
                        ? '0 1px 1px -3px rgba(0,0,0,0.3), 0 3px 6px -2px rgba(0,0,0,0.2)'
                        : '0 1px 1px -3px rgba(0,0,0,0.1), 0 3px 6px -2px rgba(0,0,0,0.01)',
                      position: 'relative',
                      backgroundColor:
                        isCurrentPlan && isPlansAndBilling
                          ? darkMode
                            ? theme.palette.grey[800]
                            : '#F8F8F8'
                          : darkMode
                            ? theme.palette.background.paper
                            : '#FFFFFF',
                      color: darkMode ? theme.palette.text.primary : '#111827',
                      border:
                        isCurrentPlan && isPlansAndBilling
                          ? 'none'
                          : darkMode
                            ? `1px solid ${theme.palette.divider}`
                            : '1px solid #E5E7EB',
                      opacity: 0,
                      animation: `fadeInUp 0.6s ease-out ${0.3 + idx * 0.15
                        }s both`,
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 5,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          display: 'flex',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h5"
                            component="h2"
                            gutterBottom
                            sx={{
                              fontWeight: 'bold',
                              fontSize: 24,
                              fontFamily: 'Power Grotesk',
                              mb: 0,
                            }}
                          >
                            {plan.title}
                          </Typography>
                          {plan.badge && (
                            <Chip
                              label={plan.badge}
                              sx={{
                                fontWeight: 'bold',
                                ...plan.badgeStyle,
                                px: 0,
                                fontSize: 13,
                                borderRadius: 16,
                                letterSpacing: 0.5,
                                height: 26,
                              }}
                            />
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            my: 2,
                          }}
                        >
                          <Typography
                            variant="h3"
                            component="p"
                            sx={{
                              fontWeight: 'bold',
                              fontSize: { xs: 32, sm: 36, md: 40 },
                              mr: 1,
                              fontFamily: 'Space Grotesk',
                              color: darkMode
                                ? theme.palette.text.primary
                                : '#111827',
                            }}
                          >
                            $
                            {Number(plan.price) % 1 === 0
                              ? Number(plan.price).toFixed(0)
                              : Number(plan.price).toFixed(2)}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: darkMode
                                ? theme.palette.text.secondary
                                : '#6B7280',
                              fontSize: { xs: 16, sm: 17, md: 18 },
                            }}
                          >
                            /month
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            minHeight: '3em',
                            color: darkMode
                              ? theme.palette.text.secondary
                              : '#6B7280',
                            fontSize: { xs: 14, sm: 15, md: 16 },
                          }}
                        >
                          {plan.priceDetail}
                        </Typography>
                      </Box>
                      <Box sx={{ my: 4 }}>
                        <Button
                          fullWidth
                          variant={plan.buttonVariant}
                          size="large"
                          disabled={isPlansAndBilling && isCurrentPlan}
                          onClick={
                            isPlansAndBilling && !isCurrentPlan
                              ? () => setActivePlanIdx(planIdx)
                              : !isPlansAndBilling
                                ? () => subscribeToPlan(plan.id, currentPlan)
                                : undefined
                          }
                          sx={{
                            ...buttonColor,
                            textTransform: 'none',
                            py: 2,
                            fontFamily: 'Space Grotesk',
                            borderRadius: 1.3,
                            fontSize: 16,
                          }}
                        >
                          {buttonText}
                        </Button>
                        {!isPlansAndBilling && plan.trialInfo && (
                          <Typography
                            variant="caption"
                            display="block"
                            align="center"
                            sx={{
                              mt: 1,
                              color: darkMode
                                ? theme.palette.text.secondary
                                : '#6B7280',
                              fontSize: { xs: 12, sm: 13 },
                            }}
                          >
                            {plan.trialInfo}
                          </Typography>
                        )}
                      </Box>
                      <Box>{renderFeatures(plan.features)}</Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                mt: 2,
              }}
            >
              {plans.map((_, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background:
                      idx === current
                        ? '#6366F1'
                        : darkMode
                          ? theme.palette.grey[600]
                          : '#E5E7EB',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                  }}
                  onClick={() => setCurrent(idx)}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Grid
            container
            spacing={4}
            alignItems="stretch"
            justifyContent="flex-start"
            sx={{
              '@keyframes fadeInUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(50px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {plans.map((plan, idx) => {
              const isPlansAndBilling = hasCurrentPlan;
              const isCurrentPlan = plan.isCurrentPlan;
              // Determine button text and color
              let buttonText = plan.buttonText;
              let buttonColor = {};
              if (isPlansAndBilling) {
                if (isCurrentPlan) {
                  buttonText = 'Your Current Plan';
                } else if (idx < activePlanIdx) {
                  buttonText = 'Downgrade';
                  buttonColor = {
                    backgroundColor: darkMode
                      ? theme.palette.background.paper
                      : '#fff',
                    color: '#4949C8',
                    borderColor: '#4949C8',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    fontWeight: 400,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#F6F6FF',
                      color: '#4949C8',
                      borderColor: '#4949C8',
                      boxShadow: 'none',
                    },
                  };
                } else {
                  buttonText = 'Upgrade';
                  buttonColor = {
                    backgroundColor: '#4949C8',
                    color: '#fff',
                    borderColor: '#4949C8',
                    '&:hover': {
                      backgroundColor: '#3A3A9E',
                      color: '#fff',
                      borderColor: '#4949C8',
                    },
                  };
                }
              } else {
                // Onboarding: botão do Basic outlined, os demais contained
                if (plan.tier === 'basic') {
                  buttonColor = {
                    backgroundColor: '#fff',
                    color: '#4949C8',
                    borderColor: '#4949C8',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    '&:hover': {
                      backgroundColor: '#F6F6FF',
                      color: '#4949C8',
                      borderColor: '#4949C8',
                    },
                  };
                } else {
                  buttonColor = {
                    backgroundColor: '#4949C8',
                    color: '#fff',
                    borderColor: '#4949C8',
                    '&:hover': {
                      backgroundColor: '#3A3A9E',
                      color: '#fff',
                      borderColor: '#4949C8',
                    },
                  };
                }
              }
              return (
                <Grid item xs={12} sm={8} md={5} lg={3} key={plan.title}>
                  <Card
                    sx={{
                      minHeight: 800,
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,

                      boxShadow: darkMode
                        ? '0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.2)'
                        : '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                      position: 'relative',
                      backgroundColor:
                        isCurrentPlan && isPlansAndBilling
                          ? darkMode
                            ? theme.palette.grey[800]
                            : '#F8F8F8'
                          : darkMode
                            ? theme.palette.background.paper
                            : '#fff',
                      color: darkMode ? theme.palette.text.primary : '#111827',
                      border:
                        isCurrentPlan && isPlansAndBilling
                          ? 'none'
                          : darkMode
                            ? `1px solid ${theme.palette.divider}`
                            : '1px solid #E5E7EB',
                      opacity: 0,
                      animation: `fadeInUp 0.6s ease-out ${0.3 + idx * 0.15
                        }s both`,
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 5,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          display: 'flex',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h5"
                            component="h2"
                            gutterBottom
                            sx={{
                              fontWeight: 'bold',
                              fontSize: 24,
                              fontFamily: 'Power Grotesk',
                              mb: 0,
                            }}
                          >
                            {plan.title}
                          </Typography>
                          {plan.badge && (
                            <Chip
                              label={plan.badge}
                              sx={{
                                fontWeight: 'bold',
                                ...plan.badgeStyle,
                                px: 0,
                                fontSize: 13,
                                borderRadius: 16,
                                letterSpacing: 0.5,
                                height: 26,
                              }}
                            />
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            my: 2,
                          }}
                        >
                          <Typography
                            variant="h3"
                            component="p"
                            sx={{
                              fontWeight: 'bold',
                              fontSize: { xs: 32, sm: 36, md: 40 },
                              mr: 1,
                              fontFamily: 'Space Grotesk',
                              color: darkMode
                                ? theme.palette.text.primary
                                : '#111827',
                            }}
                          >
                            $
                            {Number(plan.price) % 1 === 0
                              ? Number(plan.price).toFixed(0)
                              : Number(plan.price).toFixed(2)}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: darkMode
                                ? theme.palette.text.secondary
                                : '#6B7280',
                              fontSize: { xs: 16, sm: 17, md: 18 },
                            }}
                          >
                            /month
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: -1,
                            minHeight: '3em',
                            color: theme.palette.text.secondary,
                            fontSize: 14,
                          }}
                        >
                          up to {plan.priceDetail}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2, mb: 4 }}>
                        <Button
                          fullWidth
                          variant={plan.buttonVariant}
                          size="large"
                          disabled={isPlansAndBilling && isCurrentPlan}
                          onClick={
                            isPlansAndBilling && !isCurrentPlan
                              ? () => setActivePlanIdx(idx)
                              : !isPlansAndBilling
                                ? () => subscribeToPlan(plan.id, currentPlan)
                                : undefined
                          }
                          sx={{
                            ...buttonColor,
                            textTransform: 'none',
                            py: 2,
                            fontFamily: 'Space Grotesk',
                            borderRadius: 1.3,
                            fontSize: 14,
                          }}
                        >
                          {buttonText}
                        </Button>
                        {plan.title === 'Basic' && <></>}
                        {plan.title === 'The Ice Breaker' && (
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: theme.palette.text.primary,
                              fontFamily: 'Space Grotesk',
                              fontWeight: 700,
                              mt: 3,
                            }}
                          >
                            Everything in Basic, and:
                          </Typography>
                        )}
                        {plan.title === 'Pro' && (
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: theme.palette.text.primary,
                              fontFamily: 'Space Grotesk',
                              fontWeight: 700,
                              mt: 3,
                            }}
                          >
                            Everything in The Ice Breaker, and:
                          </Typography>
                        )}
                        {plan.title === 'Enterprise' && (
                          <Typography
                            sx={{
                              fontSize: 14,
                              color: theme.palette.text.primary,
                              fontFamily: 'Space Grotesk',
                              fontWeight: 700,
                              mt: 3,
                            }}
                          >
                            Everything in Pro, and:
                          </Typography>
                        )}
                        {!isPlansAndBilling && plan.trialInfo && (
                          <Typography
                            variant="caption"
                            display="block"
                            align="center"
                            sx={{
                              mt: 0,
                              color: darkMode
                                ? theme.palette.text.secondary
                                : '#6B7280',
                              fontSize: { xs: 12, sm: 13 },
                            }}
                          >
                            {plan.trialInfo}
                          </Typography>
                        )}
                      </Box>
                      <Box>{renderFeatures(plan.features)}</Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Box>
  );
};
